import {
  NewsItem,
  EventItem,
  HeroSlide,
  SiteSettings,
  AdminAccount,
  ActivityItem,
  CultureItem,
  BoardMember,
  MemberUser,
  DonationRecord,
  MediaGalleryItem,
  DocumentItem,
  AdvertisementItem,
  EventTicket,
  UserTicketPurchase,
} from '../types';

const API_BASE = '/api';

/**
 * Helper to execute API requests with error handling
 */
async function apiRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const defaultHeaders: HeadersInit = {
    'Accept': 'application/json',
  };

  if (!(options.body instanceof FormData)) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  const text = await response.text();
  let data: any;
  try {
    data = JSON.parse(text);
  } catch (err) {
    throw new Error(`Sunucu yanıtı JSON formatında değil: ${text.slice(0, 100)}`);
  }

  if (!response.ok || data.success === false) {
    throw new Error(data.message || data.error || 'İşlem başarısız oldu.');
  }

  return data as T;
}

// ==========================================
// ADMIN AUTHENTICATION API
// ==========================================

export async function loginAdminApi(username: string, password: string): Promise<{ user: AdminAccount; token?: string }> {
  const result = await apiRequest<{ success: boolean; user: AdminAccount; token?: string }>('/admin/login', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  });
  return { user: result.user, token: result.token };
}

export async function logoutAdminApi(): Promise<boolean> {
  try {
    await apiRequest('/admin/logout', { method: 'POST' });
    return true;
  } catch {
    return false;
  }
}

export async function checkAdminAuthApi(): Promise<AdminAccount | null> {
  try {
    const result = await apiRequest<{ authenticated: boolean; user: AdminAccount | null }>('/admin/auth_check');
    return result.authenticated ? result.user : null;
  } catch {
    return null;
  }
}

// ==========================================
// NEWS API
// ==========================================

export async function fetchNewsApi(): Promise<NewsItem[]> {
  const result = await apiRequest<{ success: boolean; data: NewsItem[] }>('/news');
  return result.data || [];
}

export async function createNewsApi(news: Partial<NewsItem>): Promise<{ id: string }> {
  return await apiRequest<{ success: boolean; id: string }>('/news', {
    method: 'POST',
    body: JSON.stringify(news),
  });
}

export async function updateNewsApi(id: string | number, news: Partial<NewsItem>): Promise<boolean> {
  await apiRequest(`/news?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(news),
  });
  return true;
}

export async function deleteNewsApi(id: string | number): Promise<boolean> {
  await apiRequest(`/news?id=${id}`, {
    method: 'DELETE',
  });
  return true;
}

// ==========================================
// EVENTS API
// ==========================================

export async function fetchEventsApi(): Promise<EventItem[]> {
  const result = await apiRequest<{ success: boolean; data: EventItem[] }>('/events');
  return result.data || [];
}

export async function createEventApi(event: Partial<EventItem>): Promise<{ id: string }> {
  return await apiRequest<{ success: boolean; id: string }>('/events', {
    method: 'POST',
    body: JSON.stringify(event),
  });
}

export async function updateEventApi(id: string | number, event: Partial<EventItem>): Promise<boolean> {
  await apiRequest(`/events?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(event),
  });
  return true;
}

export async function deleteEventApi(id: string | number): Promise<boolean> {
  await apiRequest(`/events?id=${id}`, {
    method: 'DELETE',
  });
  return true;
}

export async function registerEventApi(eventId: string, fullName: string, email: string, phone: string): Promise<boolean> {
  await apiRequest('/events/register', {
    method: 'POST',
    body: JSON.stringify({ eventId, fullName, email, phone }),
  });
  return true;
}

// ==========================================
// BANNERS / HERO SLIDES API
// ==========================================

export async function fetchBannersApi(): Promise<HeroSlide[]> {
  const result = await apiRequest<{ success: boolean; data: HeroSlide[] }>('/banners');
  return result.data || [];
}

export async function createBannerApi(banner: Partial<HeroSlide>): Promise<{ id: string }> {
  return await apiRequest<{ success: boolean; id: string }>('/banners', {
    method: 'POST',
    body: JSON.stringify(banner),
  });
}

export async function updateBannerApi(id: string | number, banner: Partial<HeroSlide>): Promise<boolean> {
  await apiRequest(`/banners?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(banner),
  });
  return true;
}

export async function deleteBannerApi(id: string | number): Promise<boolean> {
  await apiRequest(`/banners?id=${id}`, {
    method: 'DELETE',
  });
  return true;
}

// ==========================================
// SITE SETTINGS API
// ==========================================

export async function fetchSettingsApi(): Promise<SiteSettings> {
  const result = await apiRequest<{ success: boolean; data: SiteSettings }>('/settings');
  return result.data;
}

export async function updateSettingsApi(settings: SiteSettings): Promise<boolean> {
  await apiRequest('/settings', {
    method: 'POST',
    body: JSON.stringify(settings),
  });
  return true;
}

// ==========================================
// IMAGE & FILE UPLOAD API
// ==========================================

export async function uploadImageApi(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const fileData = reader.result as string;
        const result = await apiRequest<{ success: boolean; url: string }>('/upload', {
          method: 'POST',
          body: JSON.stringify({ fileData, fileName: file.name }),
        });
        resolve(result.url);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Dosya okunamadı.'));
    reader.readAsDataURL(file);
  });
}

// ==========================================
// ACTIVITIES API
// ==========================================

export async function fetchActivitiesApi(): Promise<ActivityItem[]> {
  try {
    const result = await apiRequest<{ success: boolean; data: ActivityItem[] }>('/activities');
    return result.data || [];
  } catch {
    return [];
  }
}

export async function createActivityApi(activity: Partial<ActivityItem>): Promise<{ id: string }> {
  return await apiRequest<{ success: boolean; id: string }>('/activities', {
    method: 'POST',
    body: JSON.stringify(activity),
  });
}

export async function updateActivityApi(id: string | number, activity: Partial<ActivityItem>): Promise<boolean> {
  await apiRequest(`/activities?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(activity),
  });
  return true;
}

export async function deleteActivityApi(id: string | number): Promise<boolean> {
  await apiRequest(`/activities?id=${id}`, { method: 'DELETE' });
  return true;
}

// ==========================================
// CULTURE API
// ==========================================

export async function fetchCultureApi(): Promise<CultureItem[]> {
  try {
    const result = await apiRequest<{ success: boolean; data: CultureItem[] }>('/culture');
    return result.data || [];
  } catch {
    return [];
  }
}

export async function createCultureApi(culture: Partial<CultureItem>): Promise<{ id: string }> {
  return await apiRequest<{ success: boolean; id: string }>('/culture', {
    method: 'POST',
    body: JSON.stringify(culture),
  });
}

export async function updateCultureApi(id: string | number, culture: Partial<CultureItem>): Promise<boolean> {
  await apiRequest(`/culture?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(culture),
  });
  return true;
}

export async function deleteCultureApi(id: string | number): Promise<boolean> {
  await apiRequest(`/culture?id=${id}`, { method: 'DELETE' });
  return true;
}

// ==========================================
// BOARD MEMBERS API
// ==========================================

export async function fetchBoardApi(): Promise<BoardMember[]> {
  try {
    const result = await apiRequest<{ success: boolean; data: BoardMember[] }>('/board');
    return result.data || [];
  } catch {
    return [];
  }
}

export async function createBoardMemberApi(member: Partial<BoardMember>): Promise<{ id: string }> {
  return await apiRequest<{ success: boolean; id: string }>('/board', {
    method: 'POST',
    body: JSON.stringify(member),
  });
}

export async function deleteBoardMemberApi(id: string): Promise<boolean> {
  await apiRequest(`/board?id=${id}`, { method: 'DELETE' });
  return true;
}

// ==========================================
// MEMBERS / USERS API
// ==========================================

export async function fetchMembersApi(): Promise<MemberUser[]> {
  try {
    const result = await apiRequest<{ success: boolean; data: MemberUser[] }>('/members');
    return result.data || [];
  } catch {
    return [];
  }
}

export async function createMemberApi(member: Partial<MemberUser>): Promise<{ id: string }> {
  return await apiRequest<{ success: boolean; id: string }>('/members', {
    method: 'POST',
    body: JSON.stringify(member),
  });
}

export async function updateMemberApi(id: string, member: Partial<MemberUser>): Promise<MemberUser> {
  const result = await apiRequest<{ success: boolean; data: MemberUser }>(`/members?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(member),
  });
  return result.data;
}

export async function deleteMemberApi(id: string): Promise<boolean> {
  await apiRequest(`/members?id=${id}`, { method: 'DELETE' });
  return true;
}

export async function loginMemberApi(email: string, password?: string): Promise<MemberUser> {
  const result = await apiRequest<{ success: boolean; user: MemberUser }>('/members/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  return result.user;
}

// ==========================================
// DONATIONS API
// ==========================================

export async function fetchDonationsApi(): Promise<DonationRecord[]> {
  try {
    const result = await apiRequest<{ success: boolean; data: DonationRecord[] }>('/donations');
    return result.data || [];
  } catch {
    return [];
  }
}

export async function createDonationApi(donation: Partial<DonationRecord>): Promise<{ id: string; receiptNo: string }> {
  const result = await apiRequest<{ success: boolean; id: string; data: DonationRecord }>('/donations', {
    method: 'POST',
    body: JSON.stringify(donation),
  });
  return { id: result.id, receiptNo: result.data.receiptNo };
}

export async function updateDonationApi(id: string, donation: Partial<DonationRecord>): Promise<boolean> {
  await apiRequest(`/donations?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify(donation),
  });
  return true;
}

export async function deleteDonationApi(id: string): Promise<boolean> {
  await apiRequest(`/donations?id=${id}`, { method: 'DELETE' });
  return true;
}

export async function validateDonationApi(payload: {
  donorName?: string;
  cardHolderName?: string;
  memberFullName: string;
  memberId?: string;
  amount: number;
  paymentMethod: string;
}): Promise<any> {
  return await apiRequest('/donations/validate', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

// ==========================================
// GALLERY API
// ==========================================

export async function fetchGalleryApi(): Promise<MediaGalleryItem[]> {
  try {
    const result = await apiRequest<{ success: boolean; data: MediaGalleryItem[] }>('/gallery');
    return result.data || [];
  } catch {
    return [];
  }
}

export async function createGalleryApi(item: Partial<MediaGalleryItem>): Promise<{ id: string }> {
  return await apiRequest<{ success: boolean; id: string }>('/gallery', {
    method: 'POST',
    body: JSON.stringify(item),
  });
}

export async function deleteGalleryApi(id: string): Promise<boolean> {
  await apiRequest(`/gallery?id=${id}`, { method: 'DELETE' });
  return true;
}

// ==========================================
// DOCUMENTS API
// ==========================================

export async function fetchDocumentsApi(): Promise<DocumentItem[]> {
  try {
    const result = await apiRequest<{ success: boolean; data: DocumentItem[] }>('/documents');
    return result.data || [];
  } catch {
    return [];
  }
}

export async function createDocumentApi(doc: Partial<DocumentItem>): Promise<{ id: string }> {
  return await apiRequest<{ success: boolean; id: string }>('/documents', {
    method: 'POST',
    body: JSON.stringify(doc),
  });
}

export async function deleteDocumentApi(id: string): Promise<boolean> {
  await apiRequest(`/documents?id=${id}`, { method: 'DELETE' });
  return true;
}

export async function downloadDocumentApi(id: string): Promise<boolean> {
  try {
    await apiRequest('/documents/download', {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
    return true;
  } catch {
    return false;
  }
}

// ==========================================
// CONTACT MESSAGES API
// ==========================================

export async function sendContactMessageApi(message: {
  fullName: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
}): Promise<boolean> {
  await apiRequest('/contact', {
    method: 'POST',
    body: JSON.stringify(message),
  });
  return true;
}

export async function fetchContactMessagesApi(): Promise<any[]> {
  try {
    const result = await apiRequest<{ success: boolean; data: any[] }>('/contact');
    return result.data || [];
  } catch {
    return [];
  }
}

export async function deleteContactMessageApi(id: string): Promise<boolean> {
  await apiRequest(`/contact?id=${id}`, { method: 'DELETE' });
  return true;
}

// ==========================================
// MEMBERSHIP APPLICATIONS API
// ==========================================

export async function submitMembershipApplicationApi(application: {
  fullName: string;
  email: string;
  phone: string;
  city?: string;
  address?: string;
  notes?: string;
}): Promise<boolean> {
  await apiRequest('/membership-applications', {
    method: 'POST',
    body: JSON.stringify(application),
  });
  return true;
}

export async function fetchMembershipApplicationsApi(): Promise<any[]> {
  try {
    const result = await apiRequest<{ success: boolean; data: any[] }>('/membership-applications');
    return result.data || [];
  } catch {
    return [];
  }
}

export async function updateMembershipApplicationApi(id: string, status: string): Promise<boolean> {
  await apiRequest(`/membership-applications?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify({ id, status }),
  });
  return true;
}

export async function deleteMembershipApplicationApi(id: string): Promise<boolean> {
  await apiRequest(`/membership-applications?id=${id}`, { method: 'DELETE' });
  return true;
}

// ==========================================
// ADVERTISEMENTS API
// ==========================================

export async function fetchAdsApi(): Promise<AdvertisementItem[]> {
  try {
    const result = await apiRequest<{ success: boolean; data: AdvertisementItem[] }>('/ads');
    return result.data || [];
  } catch {
    return [];
  }
}

export async function createAdApi(item: Partial<AdvertisementItem>): Promise<AdvertisementItem> {
  const result = await apiRequest<{ success: boolean; data: AdvertisementItem }>('/ads', {
    method: 'POST',
    body: JSON.stringify(item),
  });
  return result.data;
}

export async function updateAdApi(id: string, item: Partial<AdvertisementItem>): Promise<boolean> {
  await apiRequest(`/ads?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify({ ...item, id }),
  });
  return true;
}

export async function deleteAdApi(id: string): Promise<boolean> {
  await apiRequest(`/ads?id=${id}`, { method: 'DELETE' });
  return true;
}

export async function recordAdViewApi(id: string): Promise<boolean> {
  try {
    await apiRequest('/ads/view', {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
    return true;
  } catch {
    return false;
  }
}

// ==========================================
// EVENT TICKETS (SATIŞA SUNULAN BİLETLER) API
// ==========================================

export async function fetchEventTicketsApi(): Promise<EventTicket[]> {
  try {
    const result = await apiRequest<{ success: boolean; data: EventTicket[] }>('/event-tickets');
    return result.data || [];
  } catch {
    return [];
  }
}

export async function createEventTicketApi(item: Partial<EventTicket>): Promise<EventTicket> {
  const result = await apiRequest<{ success: boolean; data: EventTicket }>('/event-tickets', {
    method: 'POST',
    body: JSON.stringify(item),
  });
  return result.data;
}

export async function updateEventTicketApi(id: string, item: Partial<EventTicket>): Promise<boolean> {
  await apiRequest(`/event-tickets?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify({ ...item, id }),
  });
  return true;
}

export async function deleteEventTicketApi(id: string): Promise<boolean> {
  await apiRequest(`/event-tickets?id=${id}`, { method: 'DELETE' });
  return true;
}

// ==========================================
// USER TICKET PURCHASES (BİLET SATIN ALMA & ONAY) API
// ==========================================

export async function fetchTicketPurchasesApi(userId?: string): Promise<UserTicketPurchase[]> {
  try {
    const url = userId ? `/ticket-purchases?userId=${userId}` : '/ticket-purchases';
    const result = await apiRequest<{ success: boolean; data: UserTicketPurchase[] }>(url);
    return result.data || [];
  } catch {
    return [];
  }
}

export async function createTicketPurchaseApi(item: Partial<UserTicketPurchase>): Promise<UserTicketPurchase> {
  const result = await apiRequest<{ success: boolean; data: UserTicketPurchase }>('/ticket-purchases', {
    method: 'POST',
    body: JSON.stringify(item),
  });
  return result.data;
}

export async function updateTicketPurchaseApi(id: string, item: Partial<UserTicketPurchase>): Promise<boolean> {
  await apiRequest(`/ticket-purchases?id=${id}`, {
    method: 'PUT',
    body: JSON.stringify({ ...item, id }),
  });
  return true;
}

export async function deleteTicketPurchaseApi(id: string): Promise<boolean> {
  await apiRequest(`/ticket-purchases?id=${id}`, { method: 'DELETE' });
  return true;
}

export interface QrValidationResponse {
  success: boolean;
  valid: boolean;
  errorCode?: string;
  message: string;
  ticketDetails?: UserTicketPurchase;
  scannedAt?: string;
}

export async function validateTicketQrApi(qrCodeOrId: string, selectedEventName: string): Promise<QrValidationResponse> {
  try {
    return await apiRequest<QrValidationResponse>('/tickets/validate-qr', {
      method: 'POST',
      body: JSON.stringify({ qrCodeOrId, selectedEventName }),
    });
  } catch (err: any) {
    return {
      success: false,
      valid: false,
      errorCode: 'SERVER_ERROR',
      message: err.message || 'QR Kod doğrulama servisinde hata oluştu.',
    };
  }
}

// ==========================================
// SYSTEM DATABASE INSTALLATION API
// ==========================================

export async function installDatabaseApi(): Promise<boolean> {
  await apiRequest('/install', { method: 'POST' });
  return true;
}

// ==========================================
// AUDIT LOGS API
// ==========================================

export async function fetchAuditLogsApi(): Promise<any[]> {
  try {
    const result = await apiRequest<{ success: boolean; data: any[] }>('/audit-logs');
    return result.data || [];
  } catch {
    return [];
  }
}
